import { useEffect, useRef, useState } from 'react'
import { EXTERNAL_APIS } from '@/config'
import { APIRoutes } from '@/services/api.routes'
import type { Symbol } from '@/models'

interface TradingViewSymbol {
  proName: string
  title: string
}

export default function BottomMarketTicker() {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains('dark')
  )
  const [symbols, setSymbols] = useState<TradingViewSymbol[]>([])
  const [symbolsLoaded, setSymbolsLoaded] = useState(false)

  // Fetch symbols that should be shown in ticker
  useEffect(() => {
    const fetchSymbols = async () => {
      try {
        const response = await APIRoutes.listSymbols({
          showInTicker: true,
          limit: 100
        })

        // Transform symbols to TradingView format
        const tradingViewSymbols: TradingViewSymbol[] = response.symbols.map((symbol: Symbol) => ({
          proName: symbol.ticker,
          title: symbol.ticker
        }))

        setSymbols(tradingViewSymbols)
        setSymbolsLoaded(true)
      } catch (err) {
        console.error('Error fetching ticker symbols:', err)
        // Fallback to empty array if API fails
        setSymbols([])
        setSymbolsLoaded(true)
      }
    }

    fetchSymbols()
  }, [])

  // Watch for dark mode changes
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'))
    }

    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    return () => observer.disconnect()
  }, [])

  // Reload widget when dark mode changes or symbols are loaded
  useEffect(() => {
    const container = containerRef.current
    if (!container || !symbolsLoaded) return

    // Don't render if no symbols available
    if (symbols.length === 0) {
      container.innerHTML = ''
      return
    }

    // Clear existing widget
    container.innerHTML = ''

    // Create new widget with updated theme
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = EXTERNAL_APIS.TRADINGVIEW_WIDGET
    script.setAttribute('data-tradingview-ticker', 'true')
    script.innerHTML = JSON.stringify({
      symbols: symbols,
      showSymbolLogo: true,
      colorTheme: isDarkMode ? 'dark' : 'light',
      isTransparent: true,
      displayMode: 'adaptive',
      locale: 'de'
    })
    container.appendChild(script)

    return () => {}
  }, [isDarkMode, symbols, symbolsLoaded])

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40">
      <div className="backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-gray-900/60 bg-white/90 dark:bg-gray-900/90 border-t border-gray-200 dark:border-gray-700">
        <div className="w-full">
          <div className="flex items-center gap-3 py-1.5 px-2 sm:px-3">
            {/* TradingView Tape */}
            <div className="flex-1 min-w-0 w-full">
              <div className="tradingview-widget-container w-full" ref={containerRef}>
                <div className="tradingview-widget-container__widget" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


